import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { getAlertMessage } from '../constants/alertMessages';
import {
  FILE_MAX_SIZE,
  FILE_MAX_SIZE_TEXT,
  TEXTBLOCK_CAPTION_MAX_LENGTH,
  TEXTBLOCK_IMAGE_MAX_COUNT,
} from '../constants/limits';
import { UpdateTextBlockReq } from '../dto/ReqDtoRepository';
import {
  AlertPosition,
  AlertType,
  TextBlockType,
} from '../enum/EnumRepository';
import { useAuth } from './useApi/useAuth';
import { useImage } from './useApi/useImage';
import { useTextBlock } from './useApi/useTextBlock';
import { useEditMode } from './useEditMode';
import { useImageFileDropState } from './useImageFileDropState';
import { useTextIndexing } from './useTextIndexing';
import { useStandardAlertStore } from '../store/portal/alertStore';
import {
  useAuthStore,
  useAuthTokenStore,
  useEditModeStore,
} from '../store/authStore';
import { usePageOwnerIdentityStore } from '../store/pageOwnerIdentityStore';
import { useTextIndexStore } from '../store/TextIndexStore';
import { useTextViewStore } from '../store/TextViewStore';
import {
  getImageDimensions,
  removeFileExtensionWithinLimit,
} from '../util/imageUtils';
import {
  canAppendTextBlockImages,
  getRemainingTextBlockImageCount,
} from '../util/textBlockImageDrop';

type TextViewerDragHandler = (event: React.DragEvent<HTMLElement>) => void;

interface UseTextBlockImageDropResult {
  isImageDragActive: boolean;
  remainingImageCount: number;
  onDragEnter: TextViewerDragHandler;
  onDragOver: TextViewerDragHandler;
  onDragLeave: TextViewerDragHandler;
  onDrop: TextViewerDragHandler;
}

export const useTextBlockImageDrop = (): UseTextBlockImageDropResult => {
  const { isEditMode } = useEditMode();
  const { isOwner } = useAuth();
  const { isIndexingMode } = useTextIndexing();
  const activeText = useTextViewStore((state) => state.activeText);
  const hasMultiSelected = useTextViewStore((state) => state.multiSelectedText.length > 0);
  const imageCount = useTextViewStore((state) => (
    state.activeTextBlockList.filter((block) => block.type === TextBlockType.IMAGE).length
  ));
  const { createTextBlock, updateTextBlock, discardTextBlock } = useTextBlock();
  const { uploadTextBlockImage } = useImage();
  const { enqueueAlert } = useStandardAlertStore();
  const processingRef = useRef(false);
  const dropContextVersionRef = useRef(0);
  const {
    activeDropTarget,
    clearDropTarget,
    handleDropTargetDragEnter,
    handleFileDragOver,
    handleFileDragLeave,
    handleImageFileDrop,
  } = useImageFileDropState<'viewer'>();

  const canUseDropTarget = (
    isEditMode &&
    isOwner &&
    !!activeText &&
    !hasMultiSelected &&
    !isIndexingMode
  );
  const remainingImageCount = useMemo(
    () => getRemainingTextBlockImageCount(imageCount),
    [imageCount],
  );

  useEffect(() => {
    dropContextVersionRef.current += 1;
    if (!canUseDropTarget) clearDropTarget();
    return () => {
      dropContextVersionRef.current += 1;
    };
  }, [
    activeText?.id,
    canUseDropTarget,
    clearDropTarget,
  ]);

  const enqueueTopAlert = useCallback((content: string) => {
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content,
    });
  }, [enqueueAlert]);

  const processFiles = useCallback(async (files: File[]) => {
    if (processingRef.current) return;

    const state = useTextViewStore.getState();
    const textId = state.activeText?.id;
    if (!canUseDropTarget || !textId || state.multiSelectedText.length > 0) return;
    const requestedOwner = usePageOwnerIdentityStore.getState().pageOwner;
    const requestedAui = requestedOwner?.aui;
    const requestedPathname = window.location.pathname;
    const requestedContextVersion = dropContextVersionRef.current;
    const requestedOwnerAccess = (
      !!useAuthTokenStore.getState().authToken &&
      useAuthStore.getState().user?.aui === requestedAui
    );
    if (!requestedAui || !requestedOwnerAccess) return;

    const isCurrentDropContext = () => (
      dropContextVersionRef.current === requestedContextVersion &&
      useEditModeStore.getState().isEditMode &&
      !useTextIndexStore.getState().isIndexingMode &&
      useTextViewStore.getState().activeText?.id === textId &&
      useTextViewStore.getState().multiSelectedText.length === 0 &&
      usePageOwnerIdentityStore.getState().pageOwner === requestedOwner &&
      window.location.pathname === requestedPathname &&
      (
        !!useAuthTokenStore.getState().authToken &&
        useAuthStore.getState().user?.aui === requestedAui
      ) === requestedOwnerAccess
    );
    if (!isCurrentDropContext()) return;

    const currentImageCount = state.getActiveTextBlockImageCount();
    if (!canAppendTextBlockImages(currentImageCount, files.length)) {
      enqueueTopAlert(getAlertMessage('TEXTBLOCK_IMAGE_MAX_EXCEEDED', {
        maxCount: TEXTBLOCK_IMAGE_MAX_COUNT,
      }));
      return;
    }

    if (files.some((file) => file.size > FILE_MAX_SIZE)) {
      enqueueTopAlert(getAlertMessage('FILE_TOO_BIG', { maxSize: FILE_MAX_SIZE_TEXT }));
      return;
    }

    processingRef.current = true;
    clearDropTarget();

    try {
      let preparedFiles: Array<{ file: File; width: number; height: number }>;
      try {
        preparedFiles = await Promise.all(files.map(async (file) => {
          const { width, height } = await getImageDimensions(file);
          return { file, width, height };
        }));
      } catch {
        enqueueTopAlert(getAlertMessage('FAILED_TO_LOAD_IMAGE'));
        return;
      }

      for (const prepared of preparedFiles) {
        if (!isCurrentDropContext()) break;

        const createdBlock = await createTextBlock({
          textId,
          type: TextBlockType.PARAGRAPH,
        });
        if (!createdBlock) break;

        const currentState = useTextViewStore.getState();
        const blockIndex = currentState.activeTextBlockList.findIndex(
          (block) => block.id === createdBlock.id,
        );
        if (!isCurrentDropContext() || blockIndex < 0) {
          await discardTextBlock({ textBlockId: createdBlock.id });
          break;
        }

        const updateReq: UpdateTextBlockReq = {
          id: createdBlock.id,
          type: TextBlockType.IMAGE,
          index: blockIndex,
          textContent: '',
          url: '',
          updateUploadFileReq: {
            uploadFileId: createdBlock.type === TextBlockType.IMAGE
              ? createdBlock.uploadFile?.id ?? ''
              : '',
            originUrl: '',
            allowDownload: false,
            width: prepared.width,
            height: prepared.height,
          },
          caption: removeFileExtensionWithinLimit(
            prepared.file.name,
            TEXTBLOCK_CAPTION_MAX_LENGTH,
          ),
        };

        const uploadedReq = await uploadTextBlockImage(textId, updateReq, prepared.file);
        if (!uploadedReq || !isCurrentDropContext()) {
          await discardTextBlock({ textBlockId: createdBlock.id });
          break;
        }

        const updatedBlock = await updateTextBlock(uploadedReq);
        if (!updatedBlock) {
          await discardTextBlock({ textBlockId: createdBlock.id });
          break;
        }
        if (!isCurrentDropContext()) break;
      }
    } finally {
      processingRef.current = false;
    }
  }, [
    canUseDropTarget,
    clearDropTarget,
    createTextBlock,
    discardTextBlock,
    enqueueTopAlert,
    updateTextBlock,
    uploadTextBlockImage,
  ]);

  const onDragEnter = useCallback<TextViewerDragHandler>((event) => {
    handleDropTargetDragEnter(
      event,
      'viewer',
      canUseDropTarget && !processingRef.current,
    );
  }, [canUseDropTarget, handleDropTargetDragEnter]);

  const onDragOver = useCallback<TextViewerDragHandler>((event) => {
    handleFileDragOver(event, canUseDropTarget && !processingRef.current);
  }, [canUseDropTarget, handleFileDragOver]);

  const onDragLeave = useCallback<TextViewerDragHandler>((event) => {
    handleFileDragLeave(event);
  }, [handleFileDragLeave]);

  const onDrop = useCallback<TextViewerDragHandler>((event) => {
    handleImageFileDrop(
      event,
      'viewer',
      canUseDropTarget && !processingRef.current,
      processFiles,
    );
  }, [canUseDropTarget, handleImageFileDrop, processFiles]);

  return {
    isImageDragActive: activeDropTarget === 'viewer',
    remainingImageCount,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
  };
};
